/**
 * 가입 신청 승인 — 운영자가 신청 내용을 보고 받을지 결정한다.
 *
 * 두 가지가 들어온다 — 자가 가입과 「브랜드」 화면의 브랜드 추가. 둘 다 승인 대기 브랜드(brand.status = pending)이고
 * 신청한 계정이 그 브랜드의 소유자다 (261013-account-brand-membership — 대행사 가입을 없앴다)
 *
 * ★ **고객은 브랜드명·질문을 확정해 등록해야 승인된다** (운영자 결정 2026-10-11). 신청자의 요청값(요청 브랜드명·원하는 질문·
 *   남긴 말)은 **이 화면에서만** 보이고, 운영자가 고쳐 확정한 값이 판정(brand_names)과 수집(geo_query)에 들어간다.
 *   서버가 확정·등록·승인을 한 트랜잭션으로 한다(`approveSignup`). 승인 뒤에는 고객 상세로 간다
 */
import { DEFAULT_SUBSCRIPTION_NAME } from '@geo-admin/shared/contract/api/billing.ts';
import { signup } from '@geo-admin/shared/contract/api/index.ts';
import type { SignupRow } from '@geo-admin/shared/contract/api/signup.ts';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { PageTitle } from '../components/PageTitle.tsx';
import { Button } from '../components/ui/button.tsx';
import { Badge, Card, CardBody, CardHeader } from '../components/ui/card.tsx';
import { FieldError, Input, Label, Textarea } from '../components/ui/form.tsx';
import { ErrorBox, Loading } from '../components/ui/states.tsx';
import { useApi, useApiMutation } from '../lib/api.ts';
import { dateTime } from '../lib/format.ts';

function MarketingBadge({ at }: { at: string | null }) {
  return at ? <Badge tone="blue">마케팅 수신 동의 · {dateTime(at)}</Badge> : <Badge>마케팅 수신 미동의</Badge>;
}

export function SignupsPage() {
  const list = useApi(signup.listSignups);

  if (list.isLoading) return <Loading />;
  if (list.error) return <ErrorBox error={list.error} />;
  const signups = list.data!.signups;

  return (
    <>
      <PageTitle title="가입 신청" description={`브랜드 ${signups.length}건 승인 대기`} />
      {signups.length === 0 ? (
        <Card>
          <CardBody className="text-sm text-slate-500">대기 중인 신청이 없습니다.</CardBody>
        </Card>
      ) : null}
      {signups.map((s) => (
        <BrandSignupCard key={s.brandId} s={s} />
      ))}
    </>
  );
}

const lines = (v: string) =>
  v
    .split('\n')
    .map((x) => x.trim())
    .filter(Boolean);

/**
 * 고객 신청 한 건 — 왼쪽은 신청 내용(요청값), 오른쪽은 운영자가 **확정할 값**이다.
 * 확정 칸은 요청값으로 미리 채워 두지만, 운영자가 간판·플레이스 표기와 맞춰 고친 값이 판정에 들어간다
 */
function BrandSignupCard({ s }: { s: SignupRow }) {
  const navigate = useNavigate();
  const approve = useApiMutation(signup.approveSignup);
  const reject = useApiMutation(signup.rejectSignup);
  // 목표 브랜드명(판정용)은 신청자의 브랜드명(표시 이름)으로 시작한다 — 운영자가 표기를 맞춰 확정한다
  const [brandNames, setBrandNames] = useState(s.brandName);
  const [query, setQuery] = useState(s.desiredQueries[0] ?? '');
  const [samplesPerDay, setSamplesPerDay] = useState(10);
  // 구독은 늘 만든다 — 기본은 제안(결제 대기), 「결제 없이 바로 시작」이면 카드 없이 바로 이용(261015-subscription-unify)
  const [startNow, setStartNow] = useState(false);
  const [offerName, setOfferName] = useState(DEFAULT_SUBSCRIPTION_NAME);
  const [offerPrice, setOfferPrice] = useState('');
  const [offerStartOn, setOfferStartOn] = useState('');
  const names = lines(brandNames);
  const ready = names.length > 0 && query.trim().length >= 2 && offerName.trim().length > 0 && offerPrice !== '';

  return (
    <Card>
      <CardHeader
        title={
          <span className="flex flex-wrap items-center gap-2">
            {s.brandName}
            {s.requesterCompanyName ? (
              <Badge tone="slate" title="업체명(계정 회사명)">
                {s.requesterCompanyName}
              </Badge>
            ) : null}
          </span>
        }
        description={`${dateTime(s.requestedAt)} 신청`}
        actions={
          <Button
            variant="danger"
            size="sm"
            disabled={reject.isPending}
            onClick={() => {
              if (confirm(`${s.brandName} 신청을 거절합니다. 브랜드와 (다른 소속이 없는) 계정이 삭제됩니다.`))
                reject.mutate({ params: { id: s.brandId } });
            }}
          >
            거절
          </Button>
        }
      />
      <CardBody className="grid gap-5 text-sm lg:grid-cols-2">
        <div className="grid content-start gap-3">
          <div>
            <div className="text-slate-500 text-xs">신청 내용 (요청값)</div>
            <div>
              브랜드명 <b>{s.brandName}</b>
            </div>
            {s.desiredQueries.length === 0 ? (
              <div>원하는 질문 —</div>
            ) : (
              s.desiredQueries.map((q) => (
                <div key={q}>
                  원하는 질문 <b>{q}</b>
                </div>
              ))
            )}
            {s.inquiry ? <p className="mt-1 whitespace-pre-wrap text-slate-600">{s.inquiry}</p> : null}
          </div>
          <div>
            <div className="text-slate-500 text-xs">신청자(소유자)</div>
            {s.email ? (
              <>
                <div>
                  {s.name ?? '—'} · {s.phone ?? '—'}
                </div>
                <div className="text-slate-500">{s.email}</div>
                <div className="mt-1">
                  <MarketingBadge at={s.marketingAgreedAt} />
                </div>
              </>
            ) : (
              <div className="text-slate-500">계정 없음</div>
            )}
          </div>
          {s.homepageUrl || s.naverPlaceUrl || s.links.length ? (
            <div className="grid gap-0.5 text-xs">
              <div className="text-slate-500">링크</div>
              {[s.homepageUrl, s.naverPlaceUrl, ...s.links]
                .filter((u): u is string => Boolean(u))
                .map((u) => (
                  <a key={u} href={u} target="_blank" rel="noreferrer" className="truncate text-live hover:underline">
                    {u}
                  </a>
                ))}
            </div>
          ) : null}
        </div>
        <form
          className="grid content-start gap-3 rounded-lg bg-slate-50 p-3"
          onSubmit={(e) => {
            e.preventDefault();
            approve.mutate(
              {
                params: { id: s.brandId },
                body: {
                  brandNames: names,
                  query: query.trim(),
                  samplesPerDay,
                  subscription: {
                    name: offerName.trim(),
                    supplyPriceKrw: Number(offerPrice),
                    startOn: offerStartOn || null,
                    startNow,
                  },
                },
              },
              {
                onSuccess: (r) => {
                  if (r.warnings.length) alert(r.warnings.join('\n'));
                  void navigate({ to: '/admin/brands/$id', params: { id: s.brandId } });
                },
              },
            );
          }}
        >
          <div className="font-medium text-sm">확정해서 승인</div>
          <Label>
            목표 브랜드명 (한 줄에 하나 · 첫 줄이 대표 표기 — 간판·플레이스 표기와 맞추고, 영문·약칭 등 다른 표기를 더해
            주세요)
            <Textarea rows={2} value={brandNames} onChange={(e) => setBrandNames(e.target.value)} />
          </Label>
          <Label>
            질문
            <Input value={query} onChange={(e) => setQuery(e.target.value)} maxLength={300} />
          </Label>
          <Label>
            하루 샘플 수
            <Input
              type="number"
              min={0}
              max={48}
              value={samplesPerDay}
              onChange={(e) => setSamplesPerDay(Number(e.target.value))}
            />
          </Label>
          <div className="grid gap-2 rounded-md border border-slate-200 bg-white p-2">
            <div className="font-medium text-xs">구독</div>
            <div className="grid grid-cols-2 gap-2">
              {/* 구독은 위 「질문」과 묶인다 — 서버가 같은 트랜잭션에서 그 질문 id 로 만든다 (261013-subscription-query-link) */}
              <p className="col-span-2 text-slate-600 text-xs">
                이 구독의 질문{' '}
                {query.trim() ? <b>「{query.trim()}」</b> : <span className="text-bad">위에 질문을 넣어 주세요</span>}
              </p>
              <Label>
                구독 이름
                <Input value={offerName} onChange={(e) => setOfferName(e.target.value)} maxLength={100} />
              </Label>
              <Label>
                월 공급가(원, 부가세 별도 · 0 원은 무료)
                <Input
                  inputMode="numeric"
                  value={offerPrice}
                  onChange={(e) => setOfferPrice(e.target.value.replace(/\D/g, ''))}
                />
              </Label>
              <Label className="col-span-2">
                구독 개시일 (선택 — 비우면 {startNow ? '오늘' : '고객이 결제한 날'})
                <Input type="date" value={offerStartOn} onChange={(e) => setOfferStartOn(e.target.value)} />
              </Label>
            </div>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={startNow} onChange={(e) => setStartNow(e.target.checked)} />
              결제 없이 바로 시작 — 카드 없이 이용을 연다(오프라인 청구·무료). 고객이 나중에 카드를 붙이면 그때부터
              자동결제
            </label>
            {startNow ? null : (
              <p className="text-slate-500 text-xs">
                고객이 「구독 관리」에서 결제하면 그날부터 이용이 열리고 수집한다
              </p>
            )}
          </div>
          <FieldError>{(approve.error ?? reject.error)?.message}</FieldError>
          <Button type="submit" disabled={approve.isPending || !ready}>
            {startNow ? '확정하고 바로 시작' : '확정하고 구독 제안'}
          </Button>
        </form>
      </CardBody>
    </Card>
  );
}
